<template>
  <section class="content uploads" @click="openClassificationKey = null">
    <div>
      <!-- will receive custom header for different types of uploads -->
      <slot />
    </div>
    <div class="file-wrap">
      <div class="file-content">
        <div
          class="input-wrap"
          :class="{ error: $v.newCollectionEntry.data.title.$error }"
        >
          <p class="text">
            {{ $t("TITLE") }}
            <span class="required">*</span>
          </p>
          <input
            type="text"
            class="input"
            maxlength="255"
            v-model="newCollectionEntry.data.title"
            @input="checkErrors('title')"
          />
          <span class="error-text">{{ $t("ERROR_TEXT") }}</span>
        </div>
        <div class="double-wrap">
          <div class="input-wrap">
            <p class="text">{{ $t("AUTHOR") }}</p>
            <input
              type="text"
              class="input"
              v-model="newCollectionEntry.data.metadata.author"
            />
          </div>
          <div class="input-wrap">
            <p class="text">{{ $t("DATE") }}</p>
            <input
              type="date"
              class="input"
              v-model="newCollectionEntry.data.metadata.date"
            />
          </div>
        </div>
        <div class="input-wrap textarea-wrap">
          <p class="text">{{ $t("DESCRIPTION") }}</p>
          <div id="description-toolbar-container" class="toolbar" />
          <tiny-editor
            class="input textarea"
            toolbar-container="description-toolbar-container"
            :text.sync="newCollectionEntry.data.description"
          />
        </div>
        <div class="input-wrap textarea-wrap">
          <p class="text">{{ $t("INSTRUCTIONS") }}</p>
          <div id="instructions-toolbar-container" class="toolbar" />
          <tiny-editor
            class="input textarea"
            toolbar-container="instructions-toolbar-container"
            :text.sync="newCollectionEntry.data.metadata.instructions"
          />
        </div>
        <div class="input-wrap" v-for="field in fields" :key="field.id" :class="{ error:  $v.newCollectionEntry.data.metadata.dynamic_fields[field.key] && $v.newCollectionEntry.data.metadata.dynamic_fields[field.key].$error }">
          <fields
            :id="field.id"
            :type="field.field_type"
            :required="field.required"
            :value="getDynamicFieldValue(field)"
            :height="field.height"
            :title="field.title"
            :text.sync="dynamicFields[field.key]"
            :fieldKey="field.key"
            :uploadedAttachmentFile="uploadedAttachmentFile[field.key]"
            :selectionOptions="field.metadata ? field.metadata.select_options: null"
            @openUppy="openUppy"
            @addAttachment="addAttachment(field.key, $event)"
            @deleteAttachment="deleteAttachment(field.key, $event)"
            @setDynamicField="setDynamicField(field.key, $event)"
          ></fields>
        </div>
      </div>
      <div class="image-wrap">
        <h2 class="file-title">
          {{ $t("COVER_IMAGE") }}
          <span class="required">*</span>
        </h2>
        <div
          class="upload-image"
          @click="openUppy('image')"
          :class="{
            error: $v.newCollectionEntry.data.image.$error,
            'with-image': newCollectionEntry.data.image,
            'no-image-uploads': !newCollectionEntry.data.image,
          }"
        >
          <img
            v-if="newCollectionEntry.data.image"
            :src="newCollectionEntry.data.image"
          />
          <button v-else class="upload-image-button">
            <img src="@/assets/images/upload_image_btn.svg" alt="" />
          </button>
          <button class="change-image" v-if="newCollectionEntry.data.image">
            <img
              src="@/assets/images/return_arrow_icon.svg"
              alt="return icon"
              class="return-icon"
            />
            <span class="text">
              {{ $t("SWITCH_IMAGE") }}
            </span>
          </button>
        </div>

        <template v-if="libraryMetadata.isEnableSocialMedia">
          <label class="enable-social-media-container">
            <input
              type="checkbox"
              v-model="newCollectionEntry.data.metadata.isEnableSocialMedia"
            />
            <span class="checkmark-popup"></span>
            <span class="text">{{
              $t("ALLOW_SHARING_ON_SOCIAL_NETWORKS")
            }}</span>
          </label>
        </template>
        <span class="error-text"> {{ $t("ERROR_TEXT") }}</span>
        <div class="grid-2-columns">
          <div
            v-for="keyClassification in classificationTree"
            :key="keyClassification.id"
          >
            <div
              class="drop-down-wrap menu-box"
              :class="{
                open: openClassificationKey === keyClassification.id,
              }"
            >
              <p class="text classification-title">
                {{ keyClassification.title }}
                <span v-show="keyClassification.required" class="required">*</span>
              </p>
              <div class="inner-wrap">
                <img :src="getSrc(keyClassification.title)" />
                <div
                  class="input drop-down"
                  :class="{
                    selected: openClassificationKey === keyClassification.id,
                    error: checkClassificationsError(keyClassification.title),
                  }"
                  @click.stop="opensMenu(keyClassification.id)"
                >
                  <p
                    class="text"
                    :class="{
                      'default-text':
                        !newCollectionEntry.data.classifications.length,
                    }"
                  >
                    {{ createMenuHeader(keyClassification.title) }}
                  </p>
                  <img
                    src="@/assets/images/arrow_next_page.svg"
                    class="arrow-down"
                  />
                </div>
                <span class="error-text">{{ $t("ERROR_TEXT") }}</span>
                <div v-if="keyClassification.values" class="dropdown-content">
                  <ul class="dropdown-list">
                    <classifications-details
                      v-for="value in keyClassification.values"
                      :key="value.id"
                      :classifications="value"
                      :classificationKey="keyClassification.title"
                      :collectionEntryClassifications="
                        newCollectionEntry.data.classifications
                      "
                      @updateClassifications="updateClassifications($event)"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="buttons-wrap rtl-reverse">
      <p v-if="publishFailed">{{ $t("PLEASE_TRY_AGAIN") }}</p>
      <button
        v-if="isContributor && routeName === 'uploads'"
        class="common-button publish-button send-for-approval"
        :class="{
          'disable tooltip-btn': !togglePublishButton || missingHeader,
        }"
        @click="sendToApproval"
        :data-text="disableTooltip"
      >
        <span>{{ $t("SEND_TO_APPROVAL") }}</span>
        <img
          src="@/assets/images/send-white.svg"
          alt="send arrow"
          class="send-icon"
        />
      </button>
      <button
        v-else
        data-testid="publishButton"
        class="common-button publish-button"
        @click="publish()"
        :class="{
          'disable tooltip-btn': !togglePublishButton || missingHeader,
        }"
        :data-text="disableTooltip"
      >
        {{ publishOrEdit }}
      </button>
      <button
        v-if="selectedEntry"
        class="common-button cancel-button"
        @click="returnOrCancelFunc"
      >
        {{ returnOrCancel }}
      </button>
    </div>
    <sent-to-approval-popup
      v-if="openSentToApprovalPopup"
      @close-popup="closeSentToApprovalPopup"
    />
  </section>
</template>

<script>
import Vue from 'vue'
import { permissionTypes, publishOptions } from '@/constants.js'
import { mapState, mapActions } from 'vuex'
import ClassificationsDetails from '@/components/classifications-details'
import SentToApprovalPopup from '@/components/sent-to-approval-popup'
import { required } from 'vuelidate/lib/validators'
import * as dayjs from 'dayjs'
import { getClassificationIcon } from '@/utilityFunctions'
import TinyEditor from '@/components/tiny-editor'
import Fields from '@/components/fields'

export default {
  components: { ClassificationsDetails, SentToApprovalPopup, TinyEditor, Fields },
  data () {
    return {
      newCollectionEntry: {
        data: {
          title: null,
          description: null,
          classifications: [],
          image: null,
          metadata: {
            author: null,
            author_uuid: null,
            date: null,
            instructions: null,
            fileName: null,
            fileType: null,
            isEnableSocialMedia: null,
            serviceUrl: null,
            dynamic_fields: {}
          }
        },
        object: {
          content_type: null,
          url: null
        }
      },
      openClassificationKey: null,
      submitStatus: null,
      publishFailed: null,
      classificationKeysTouch: {},
      openSentToApprovalPopup: false,
      type: this.$route.params.type,
      routeName: this.$route.name
    }
  },
  created () {
    this.classificationTree.map(classification => {
      if (classification.required) {
        this.classificationKeysTouch[classification.title] = false
      }
    })
  },
  // if selectedEntry exists, sets title and image accordingly
  mounted () {
    if (this.selectedEntry) {
      if (this.selectedEntryOrigin === 'collectionEntry') {
        this.newCollectionEntry = JSON.parse(JSON.stringify(this.selectedEntry))
        if (!this.newCollectionEntry.data.metadata) {
          this.newCollectionEntry.data.metadata = {}
        }
      } else {
        this.newCollectionEntry.data.title = this.selectedEntry.data.title
        this.newCollectionEntry.data.image = this.selectedEntry.data.image
        this.newCollectionEntry.data.metadata.date = this.selectedEntry.data.metadata?.date ?? dayjs().format('YYYY-MM-DD')
        this.newCollectionEntry.data.metadata.author = this.selectedEntry.data.metadata?.author ?? this.user.firstName + ' ' + this.user.lastName
        this.newCollectionEntry.data.metadata.author_uuid = this.selectedEntry.data.metadata?.author_uuid ?? this.user.uuid
        if (this.selectedEntry.object) {
          this.newCollectionEntry.object = JSON.parse(JSON.stringify(this.selectedEntry.object))
        }
      }
      this.newCollectionEntry.data.metadata.dynamic_fields = this.selectedEntry.data.metadata?.dynamic_fields || {}
    } else {
      this.newCollectionEntry.data.metadata.date = dayjs().format('YYYY-MM-DD')
      this.newCollectionEntry.data.metadata.author = this.user.firstName + ' ' + this.user.lastName
      this.newCollectionEntry.data.metadata.author_uuid = this.user.uuid
      if (this.$route.params.type === 'embedded-audio') {
        this.newCollectionEntry.object.content_type = 'AUDIO'
      } else {
        this.newCollectionEntry.object.content_type = publishOptions.find(item => item.type === this.$route.params.type).name
        if (this.newCollectionEntry.object.content_type === 'URL') {
          this.newCollectionEntry.object.content_type = 'EXTERNAL'
        }
      }
    }
  },
  computed: {
    ...mapState({
      classificationTree: state => state.classifications.classifications,
      selectedEntry: state => state.collections.selectedEntry,
      selectedEntryOrigin: state => state.collections.selectedEntryOrigin,
      librarySlug: state => state.base.library.slug,
      libraryMetadata: state => state.base.library.metadata,
      uploadFile: state => state.uploads.uploadFile,
      uploadUrl: state => state.uploads.uploadUrl,
      user: state => state.base.user,
      userRoles: state => state.base.userRoles,
      uploadImageUrl: state => state.uploads.uploadImageUrl,
      fields: state => state.collections.fields,
      selectedVideo: state => state.youtube.selectedVideo,
      uploadedAttachmentFile: state => state.uploads.uploadedAttachmentUrls
    }),
    isContributor () {
      return this.userRoles?.includes(permissionTypes.CONTRIBUTOR)
    },
    dynamicFields () {
      return this.newCollectionEntry.data.metadata.dynamic_fields
    },
    // toggles publish button, return true after filling all required fields
    togglePublishButton () {
      const validClassifications = []
      const validDynamicFields = []
      let isValid = false
      for (const key of Object.keys(this.classificationKeysTouch)) {
        const isError = this.checkClassifications(key)
        validClassifications.push(!isError)
      }
      for (const field of this.fields) {
        const isError = (field.required && !this.dynamicFields[field.key])
        validDynamicFields.push(!isError)
      }
      isValid = validClassifications.every(el => el) && validDynamicFields.every(el => el)
      if (isValid && !this.$v.$invalid) {
        return true
      } else {
        return false
      }
    },
    publishOrEdit () {
      return this.routeName === 'edit-collection' ? this.$t('SAVE') : this.$t('PUBLISH')
    },
    returnOrCancel () {
      return this.routeName === 'edit-collection' ? this.$t('CANCEL') : this.$t('RETURN')
    },
    missingHeader () {
      if ((this.type === 'file' || this.type === 'audio') && (!this.uploadFile && !this.newCollectionEntry.data.metadata.fileName)) {
        return true
      } else if (this.type === 'url' && (!this.uploadUrl && !this.newCollectionEntry.object.url)) {
        return true
      } else if (this.type === 'youtube' && !this.selectedVideo) {
        return true
      } else {
        return false
      }
    },
    disableTooltip () {
      // in audio and file upload if all required fields are filled - 'file needed' tooltip is displayed
      if ((this.type === 'file' || this.type === 'audio') && !this.uploadFile && this.togglePublishButton) {
        return this.$t('UPLOAD_FILE_IS_NEEDED')
      }
      return this.$t('FILL_ALL_REQUIRED_FIELDS')
    }
  },
  watch: {
    uploadImageUrl () {
      if (this.uploadImageUrl) {
        this.newCollectionEntry.data.image = this.uploadImageUrl
      }
    }
  },
  methods: {
    ...mapActions(['resetUploadData', 'resetUploadAttachment', 'postNewCollectionEntry', 'updateCollectionEntry']),
    getDynamicFieldValue (field) {
      // Get value of dynamic_field in entry
      if (this.newCollectionEntry.data.metadata?.dynamic_fields) {
        return this.newCollectionEntry.data.metadata.dynamic_fields[field.key]
      } else {
        return ''
      }
    },
    // sets classifications if selectedEntryOrigin is collectionEntry
    checkURLisImage (url) {
      return (url.match(/\.(jpeg|jpg|gif|png)$/) != null)
    },
    // gets icon matching classification type or default icon
    getSrc (title) {
      return getClassificationIcon(title)
    },
    // check if a suitable classification is selected for a particular title
    checkClassifications (title) {
      if (!this.newCollectionEntry.data.classifications) {
        return true
      } else {
        for (const val of this.newCollectionEntry.data.classifications) {
          if (val.key === title) {
            return false
          }
        }
      }
      return true
    },
    checkClassificationsError (title) {
      // In case the classification is touch we will check if a suitable classification is selected
      if (this.classificationKeysTouch[title]) {
        return this.checkClassifications(title)
      }
      return false
    },
    // return menu header - if values are selected num of selected values is returned
    createMenuHeader (title) {
      if (this.newCollectionEntry.data.classifications.length) {
        let length = 0
        for (const value of this.newCollectionEntry.data.classifications) {
          if (value.key === title) {
            length++
          }
        }
        if (length) {
          return length + ' ' + this.$t('SELECTED')
        }
      }
      // If no value is selected
      return this.$t('SELECT_CLASSIFICATION') + ' ' + title
    },
    updateClassifications (classifications) {
      const key = classifications.values[classifications.values.length - 1].key
      this.classificationKeysTouch[key] = true
      this.$set(this.newCollectionEntry.data.classifications, classifications)
    },
    openUppy (typeOfUpload) {
      this.$emit('openUppy', typeOfUpload)
    },
    // toggles classification menus
    opensMenu (id) {
      this.openClassificationKey = this.openClassificationKey === id ? null : id
      this.scrollToEnd()
    },
    scrollToEnd () {
      this.$el
        // I removed -1 for stopping the scrolling
        // .getElementsByClassName('menu-box')[this.$el.getElementsByClassName('menu-box').length - 1].scrollIntoView({ behavior: 'smooth' })
        .getElementsByClassName('menu-box')[this.$el.getElementsByClassName('menu-box').length].scrollIntoView({ behavior: 'smooth' })
      setTimeout(() => {
      }, 50)
    },
    checkErrors (input) {
      this.$v.newCollectionEntry.data[input].$touch()
    },
    returnOrCancelFunc () {
      if (this.selectedEntryOrigin === 'collectionEntry') {
        this.$router.go(-1)
      } else {
        this.$emit('openClasseUnitSearch')
      }
    },
    async publish (isForApproval = false) {
      if (!this.togglePublishButton || this.missingHeader) {
        return
      }
      this.publishFailed = null
      await this.$v.$touch()
      if (this.$v.newCollectionEntry.$error) {
        this.submitStatus = 'ERROR'
      } else {
        if (this.dynamicFields && Object.keys(this.dynamicFields).length === 0) {
          delete this.newCollectionEntry.data.metadata.dynamic_fields
        }
        this.submitStatus = 'PENDING'
        if (this.uploadFile) {
          this.newCollectionEntry.data.metadata.fileName = this.uploadFile.name
          this.newCollectionEntry.data.metadata.fileType = this.uploadFile.type
          delete this.newCollectionEntry.data.metadata.serviceUrl
          this.newCollectionEntry.object.url = this.uploadFile.uploadURL
        }
        if (this.uploadUrl) {
          this.newCollectionEntry.object.url = this.uploadUrl
          if (!this.checkURLisImage(this.uploadUrl)) {
            const getServiceUrl = this.uploadUrl.slice(0, this.uploadUrl.indexOf(this.uploadUrl.split('/')[3]) - 1)
            if (this.$route.params.type === 'embedded-audio') {
              this.newCollectionEntry.data.metadata.isEmbeddedAudio = true
            } else if (this.uploadUrl.includes('youtu.be')) {
              const index = this.uploadUrl.indexOf('youtu.be')
              const oldUrl = this.uploadUrl
              this.newCollectionEntry.object.url = 'https://www.youtube.com/embed/' + oldUrl.slice(index + 9)
              this.newCollectionEntry.object.content_type = 'VIDEO_YOUTUBE'
            } else {
              this.newCollectionEntry.data.metadata.serviceUrl = getServiceUrl
            }
          }
          delete this.newCollectionEntry.data.metadata.fileName
          delete this.newCollectionEntry.data.metadata.fileType
        }
        if (this.selectedVideo) {
          this.newCollectionEntry.object.url = this.selectedVideo.videoURL
          delete this.newCollectionEntry.data.metadata.fileName
          delete this.newCollectionEntry.data.metadata.fileType
        }
        if (!this.newCollectionEntry.data.description) {
          this.newCollectionEntry.data.description = ''
        }
        // the collection update is saved in newCollectionEntry
        if (this.routeName === 'edit-collection') {
          this.updateCollectionEntry(this.newCollectionEntry).then(() => {
            console.log('update success')
          }).catch((e) => {
            this.publishFailed = true
            return false
          })
        } else {
          this.postNewCollectionEntry(this.newCollectionEntry).then(() => {
            console.log('upload success')
          }).catch((e) => {
            this.publishFailed = true
            return false
          })
        }
      }
      this.resetUploadData()
      if (isForApproval) {
        this.openSentToApprovalPopup = true
      } else {
        if (this.routeName === 'edit-collection') {
          this.$router.go(-1)
        } else {
          this.$router.push({ name: 'baseLibrary' })
        }
      }
    },
    sendToApproval () {
      this.publish(true)
    },
    closeSentToApprovalPopup () {
      this.$router.go(-1)
    },
    addAttachment (key, data) {
      // if the dynamic field type is 'ATTACHMENS' then we add a array of all the attachments added.
      // so we need to initialize the array if there isn't one already
      if (!this.newCollectionEntry.data.metadata.dynamic_fields[key]) {
        Vue.set(this.newCollectionEntry.data.metadata.dynamic_fields, key, [])
      }
      Vue.set(this.newCollectionEntry.data.metadata.dynamic_fields[key], this.newCollectionEntry.data.metadata.dynamic_fields[key].length, data)
      this.touchDynamicField(key)
      this.resetUploadAttachment(key)
    },
    deleteAttachment (key, index) {
      this.newCollectionEntry.data.metadata.dynamic_fields[key].splice(index, 1)
      if (this.newCollectionEntry.data.metadata.dynamic_fields[key].length === 0) {
        Vue.delete(this.newCollectionEntry.data.metadata.dynamic_fields, key)
        this.touchDynamicField(key)
      }
    },
    setDynamicField (key, val) {
      if (!val) {
        this.$delete(this.newCollectionEntry.data.metadata.dynamic_fields, key)
        this.touchDynamicField(key)
        return
      }
      this.$set(this.newCollectionEntry.data.metadata.dynamic_fields, key, val)
      this.touchDynamicField(key)
    },
    touchDynamicField (key) {
      if (this.$v.newCollectionEntry.data.metadata.dynamic_fields[key]) {
        this.$v.newCollectionEntry.data.metadata.dynamic_fields[key].$touch()
      }
    }
  },
  validations () {
    const validators = {
      newCollectionEntry: {
        data: {
          title: { required },
          image: { required },
          metadata: { dynamic_fields: {} }
        }
      }
    }
    this.fields.map(field => {
      if (field.required) {
        if (field.field_type === 'ATTACHMENTS') {
          validators.newCollectionEntry.data.metadata.dynamic_fields[field.key] = { required }
        } else validators.newCollectionEntry.data.metadata.dynamic_fields[field.key] = { required }
      }
    })
    return validators
  }

}
</script>

<style lang="scss" scoped>
input[type="date"] {
  &:before {
    background-image: url("../assets/images/calendar_grey.svg");
  }

  &:focus, &:focus-within, &:hover {
    &:before {
      background-image: url("../assets/images/calendar_blue.svg");
    }
  }
}

.enable-social-media-container {
  justify-content: end;
  margin: 0.25rem 0;
  align-items: center;
}

.enable-social-media-container .text {
  font-size: 0.8rem;
  color: #7C7979;
  margin: 0 0.2rem;
}
.classification-title{
  margin-top: 10px; margin-bottom: 5px;
}
.send-icon{
  width: 24px; height: 20px;
  .ltr &{
    transform: rotate(180deg);
  }
}
.publish-button.disable{
  pointer-events: auto;
  &:hover{transform: unset;}
}
.disable.tooltip-btn{
  &:before{
    left: 0; white-space: normal;
  }
}
</style>
